/**
 * Shared, de-duplicated PhishTrainer play-settings fetch.
 *
 * The play home (`/play`) renders TWO independent components that each need the
 * workspace's PhishTrainer settings: `PlayerBrandLogo` (chrome → branding.logoPath)
 * and `ModePreferenceCard` (home → gameplay.mode.{allowUserToggle,defaultMode}).
 * Before this helper they fetched `/api/phishtrainer/settings` SEPARATELY, so
 * every play page made the settings request twice. The second (later) call fires
 * in the middle of the player-nav's RSC-prefetch burst and, on a small FE origin
 * (staging = 2 instances), intermittently returns a Cloudflare 502 — surfacing a
 * "Failed to load resource: 502" console error even though both callers degrade
 * gracefully (default logo / hidden mode card).
 *
 * Routing both through this module-level memoised fetch collapses the two
 * requests into ONE (the early caller wins; the later one reuses the in-flight /
 * cached promise), which removes the redundant load AND the duplicate-request
 * 502. The 30s TTL is long enough to dedupe a single page's concurrent mounts
 * yet short enough that a mid-session admin settings change is picked up on the
 * next interval. A failed fetch clears the memo so the next caller can retry.
 */
import { pfetch } from './play-api'

/** Shape we read from `/api/phishtrainer/settings` — kept loose on purpose; the
 *  proxy returns the full settings tree and callers pick the fields they need. */
export interface PlaySettingsResponse {
  data?: {
    // primaryColor drives the player-portal accent bar via PlayerBrandAccent, which
    // publishes it as --pt-brand. It was returned by the endpoint all along but not
    // declared here, so the first consumer to read it failed to typecheck.
    // defaultPalette / defaultThemeMode are the WORKSPACE default the admin picked;
    // the player seeds them only when the learner has no saved choice of their own.
    // Typed `string | null` rather than the narrow unions on purpose — the jsonb
    // column predates any validation, so it can hold a key that is not in the
    // catalog, and the runtime guards (isThemePalette / isThemeMode) are what
    // decide. Declaring the union here would make TS vouch for a value nobody checked.
    branding?: {
      logoPath?: string
      primaryColor?: string
      defaultPalette?: string | null
      defaultThemeMode?: string | null
    }
    // `gameplay` carries far more than `mode` — scoring, enableWhyFeature,
    // mobileCardStyle and others. The index signature keeps this genuinely loose, as
    // the comment above always claimed: consumers cast the fields they need rather
    // than this file tracking the whole settings tree.
    gameplay?: {
      mode?: { allowUserToggle?: boolean; defaultMode?: string }
      [k: string]: unknown
    }
    [k: string]: unknown
  }
}

const TTL_MS = 30_000

/**
 * The memo lives on globalThis rather than in module scope.
 *
 * HONEST HISTORY, because the first version of this comment asserted a cause that was
 * wrong. It claimed Next.js duplicated this module across five chunks and that each copy
 * had its own `inflight`. That came from grepping the built output for `30000|3e4`, which
 * matches masses of unrelated code. Checking properly — grepping for this MEMO_KEY —
 * finds the module in exactly ONE chunk. There was no duplication.
 *
 * WHAT WAS ACTUALLY HAPPENING. /play/game issued three GET /api/phishtrainer/settings,
 * and only one of them came through here. The other two are direct fetches that bypass
 * this helper entirely: PlayerShell's mount-time read and SettingsContext's. Both are now
 * routed through here, which is the real fix; /play (which mounts neither) always issued
 * exactly one, proving the memo worked all along.
 *
 * globalThis is kept because it costs nothing and is robust if this module is ever loaded
 * in more than one context — but it fixed nothing on its own, and the comment should not
 * imply otherwise.
 *
 * NOT memoised: PlayerShell's mid-round refetch (PlayerShell.tsx:627). That one exists
 * specifically to read settings that changed during a round, so it must bypass the TTL.
 */
const MEMO_KEY = '__hfPlaySettingsMemo__'

interface PlaySettingsMemo {
  inflight: Promise<PlaySettingsResponse> | null
  fetchedAt: number
}

function memo(): PlaySettingsMemo {
  const g = globalThis as Record<string, unknown>
  if (!g[MEMO_KEY]) {
    g[MEMO_KEY] = { inflight: null, fetchedAt: 0 } satisfies PlaySettingsMemo
  }
  return g[MEMO_KEY] as PlaySettingsMemo
}

/**
 * Fetch the play-portal settings once and share the result across all callers
 * for {@link TTL_MS}. Throws on a non-ok response (the memo is cleared so a
 * subsequent caller retries); callers are expected to `.catch` and fall back.
 */
export function getPlaySettings(): Promise<PlaySettingsResponse> {
  const m = memo()
  const now = Date.now()
  if (m.inflight && now - m.fetchedAt < TTL_MS) return m.inflight

  m.fetchedAt = now
  m.inflight = pfetch('/api/phishtrainer/settings')
    .then((r) => {
      if (!r.ok) throw new Error(`settings ${r.status}`)
      return r.json() as Promise<PlaySettingsResponse>
    })
    .catch((e) => {
      // Allow the next caller to retry rather than caching the failure. Cleared on the
      // SHARED slot, so every duplicated module instance sees the retry too.
      memo().inflight = null
      throw e
    })

  return m.inflight
}
